/**
 * SDS Canvas operations module — ported from Designer
 * Handles canvas background, text layers, and image layer operations
 */

import { store } from '../../../core/store.js'
import { createLogger } from '../../../utils/logger.js'


const log = createLogger('sds-canvas')

function getSDK() {
  return store.getState('design.sdkInstance')
}

// ========================================
// Canvas Background APIs
// ========================================

export function setBackgroundColor(color) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setBackgroundColor(color)
    log.info('Background color set:', color)
  } catch (error) {
    log.warn('Failed to set background color:', error)
  }
}

export function getBackgroundColor() {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return null

  try {
    return sdk.sarto.getBackgroundColor()
  } catch (error) {
    log.warn('Failed to get background color:', error)
    return null
  }
}

export function clearCanvas() {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.clearCanvas()
    log.info('Canvas cleared')
  } catch (error) {
    log.warn('Failed to clear canvas:', error)
  }
}

// ========================================
// Text layer APIs
// ========================================

export function addText() {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.addTextLayer()
    log.info('Text added')
  } catch (error) {
    log.warn('Failed to add text:', error)
  }
}

export function setFontFamily(fontFamily, callback) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setTextFont(fontFamily, (result) => {
      if (result.status === 'success') {
        log.info('Font family set:', fontFamily)
      }
      if (callback) callback(result)
    })
  } catch (error) {
    log.warn('Failed to set font family:', error)
    if (callback) callback({ status: 'error', message: error.message })
  }
}

export function setFontSize(fontSize) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setTextFontSize(fontSize)
    log.info('Font size set:', fontSize)
  } catch (error) {
    log.warn('Failed to set font size:', error)
  }
}

export function setFontColor(color) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setTextColor(color)
    log.info('Font color set:', color)
  } catch (error) {
    log.warn('Failed to set font color:', error)
  }
}

export function setTextAlign(type) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setTextAlign(type)
    log.info('Text align set:', type)
  } catch (error) {
    log.warn('Failed to set text align:', error)
  }
}

// ========================================
// Image layer APIs
// ========================================

export function addSDKImage(file, callback) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    const addFn = sdk.sarto.addImageLayer || sdk.sarto.addSDKImage
    if (!addFn) {
      log.warn('No image add method found on SDK')
      if (callback) callback({ status: 'error', message: 'No image add method' })
      return
    }
    addFn.call(sdk.sarto, file, (result) => {
      if (result.status === 'success') {
        log.info('Image added successfully')
      } else {
        log.error('Failed to add image:', result)
      }
      if (callback) callback(result)
    })
  } catch (error) {
    log.warn('Failed to add SDK image:', error)
    if (callback) callback({ status: 'error', message: error.message })
  }
}

export function addMaterial(id, callback) {
  const sdk = getSDK()
  if (!sdk) return

  try {
    sdk.addMaterial(id, (result) => {
      if (result.status === 'success') {
        log.info('Material added:', result.data)
      }
      if (callback) callback(result)
    })
  } catch (error) {
    log.warn('Failed to add material:', error)
    if (callback) callback({ status: 'error', message: error.message })
  }
}

export function setImageFit(type) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setImageFit(type)
    log.info('Image fit set:', type)
  } catch (error) {
    log.warn('Failed to set image fit:', error)
  }
}

export function setTiling(isForce, type) {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.setTiling(isForce, type)
    log.info('Tiling set:', type)
  } catch (error) {
    log.warn('Failed to set tiling:', error)
  }
}

export function cancelTiling() {
  const sdk = getSDK()
  if (!sdk || !sdk.sarto) return

  try {
    sdk.sarto.cancelTiling()
    log.info('Tiling cancelled')
  } catch (error) {
    log.warn('Failed to cancel tiling:', error)
  }
}

export default {
  setBackgroundColor,
  getBackgroundColor,
  clearCanvas,
  addText,
  setFontFamily,
  setFontSize,
  setFontColor,
  setTextAlign,
  addSDKImage,
  addMaterial,
  setImageFit,
  setTiling,
  cancelTiling,
}
